"use client";

import { useState } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";

import { Icon, type IconName } from "@/components/ui/icon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { useInlineNodeDecoration } from "../../lib/use-inline-node-decoration";
import { VariableFallbackPopover } from "./fallback-popover";
import type {
  VariableAttrs,
  VariableCategory,
} from "./extension";

const CATEGORY_ICON: Readonly<Record<VariableCategory, IconName>> = Object.freeze({
  person: "IconCirclePerson",
  sender: "IconEmail1",
  job: "IconSuitcaseWork",
});

export function VariableChip(props: NodeViewProps) {
  const { node, editor, getPos, selected, updateAttributes, deleteNode } =
    props;
  const attrs = node.attrs as VariableAttrs;
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [gearKey, setGearKey] = useState(0);

  const categoryIcon = CATEGORY_ICON[attrs.category] ?? CATEGORY_ICON.person;
  const display = attrs.label || attrs.key;
  const editable = editor.isEditable;

  // Bold / italic / underline / link applied to the chip are real formatting:
  // the backend renderer wraps the resolved value in <strong>/<em>/<u>/<a>, so
  // the chip mirrors the same styling here and in the live preview. A blue
  // outline marks it as part of the current selection.
  const { isSelected, isBold, isItalic, isUnderline, isLink } =
    useInlineNodeDecoration({ node, editor, getPos, selected });

  return (
    <NodeViewWrapper
      as="span"
      data-category={attrs.category}
      data-fallback-policy={attrs.fallback_policy}
      className="inline-flex items-baseline align-baseline"
    >
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (!editable && next) return;
          setOpen(next);
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={!editable}
            aria-label={display}
            onMouseEnter={() => {
              setHovered(true);
              setGearKey((k) => k + 1);
            }}
            onMouseLeave={() => setHovered(false)}
            className={cn(
              "group/chip mx-0.5 inline-flex shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5",
              "text-xs leading-tight whitespace-nowrap select-none",
              isBold ? "font-bold" : "font-medium",
              "transition-colors cursor-pointer",
              "disabled:cursor-default disabled:opacity-70",
              "bg-[rgba(124,58,237,0.08)] border-[rgba(124,58,237,0.22)] text-[#7c3aed]",
              "hover:bg-[rgba(124,58,237,0.14)]",
              "dark:bg-[rgba(200,179,240,0.06)] dark:border-[rgba(200,179,240,0.15)] dark:text-[#c8b3f0]",
              "dark:hover:bg-[rgba(200,179,240,0.12)]",
              // Selection state — swap the border to blue (normal width) so
              // it's clear the chip is part of the current selection without a
              // heavy ring.
              isSelected &&
                "!border-[#2b5df3] bg-[rgba(43,93,243,0.10)] dark:bg-[rgba(43,93,243,0.16)]",
            )}
          >
            <span className="relative flex size-3 items-center justify-center">
              <span
                className={cn(
                  "absolute inset-0 flex items-center justify-center transition-opacity duration-150",
                  hovered ? "opacity-0" : "opacity-80",
                )}
              >
                <Icon name={categoryIcon} size={12} />
              </span>
              <span
                key={gearKey}
                className={cn(
                  "absolute inset-0 flex items-center justify-center transition-opacity duration-150",
                  hovered ? "opacity-80 animate-[var-gear-spin_0.4s_ease-out_forwards]" : "opacity-0",
                )}
              >
                <Icon name="IconSettingsGear2" size={12} />
              </span>
            </span>
            <span
              className={cn(
                isItalic && "italic",
                (isUnderline || isLink) && "underline",
                // A link shows blue text + blue underline (underline color
                // follows text color) so it's distinct from a plain underline.
                isLink && "text-[#86b3ff]",
              )}
            >
              {display}
            </span>
            {editable && (
              <Icon
                name="IconCrossMedium"
                size={10}
                className="opacity-50 hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteNode();
                }}
              />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={6}
          className="w-auto p-0"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
          }}
        >
          <VariableFallbackPopover
            attrs={attrs}
            onSave={(next) => {
              updateAttributes(next);
              setOpen(false);
            }}
            onRemove={() => {
              deleteNode();
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </NodeViewWrapper>
  );
}
